import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuthToken, BASE_URL } from './apiClient';

/**
 * RTK Query owns every read the dashboards make.
 *
 * The sidebar badges, the top-bar revenue pill and the overview KPIs all want the
 * same summary — asking for it by the same cache key means one request serves all
 * three, and changing the date range refetches once for the whole screen.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers) => {
    const token = getAuthToken();
    if (token) headers.set('authorization', `Bearer ${token}`);
    return headers;
  },
});

/** Turns the API's `{ error }` body into a plain message the UI can render. */
const baseQuery = async (args, apiCtx, extraOptions) => {
  const result = await rawBaseQuery(args, apiCtx, extraOptions);
  if (result.error) {
    const { status, data } = result.error;
    result.error.message =
      status === 'FETCH_ERROR'
        ? "Can't reach the server. Check that the API is running."
        : data?.error || `Request failed (${status})`;
  }
  return result;
};

/** Strips empty values so a blank filter never reaches the query string. */
const qs = (params = {}) => {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out.append(k, v);
  }
  const s = out.toString();
  return s ? `?${s}` : '';
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['Transactions', 'Payout', 'Geo', 'Leads', 'Accounts', 'Users', 'Influencers', 'Options'],
  endpoints: (build) => ({
    /** Everything a dashboard needs in one round trip. */
    summary: build.query({
      query: ({ range = 30, channel } = {}) => `/transactions/summary${qs({ range, channel })}`,
      providesTags: ['Transactions'],
    }),

    /** The ledger table. `params` mirrors the query string one-for-one. */
    transactions: build.query({
      query: (params = {}) => `/transactions${qs(params)}`,
      providesTags: ['Transactions'],
    }),

    /** Distinct values behind the ledger's filter dropdowns. */
    transactionFilters: build.query({
      query: ({ channel, range } = {}) => `/transactions/filters${qs({ channel, range })}`,
      providesTags: ['Transactions'],
    }),

    /** Per-channel influencer payouts, plus the grand total for the banner. */
    payout: build.query({
      query: ({ range = 30, channel } = {}) => `/influencers/payout${qs({ range, channel })}`,
      providesTags: ['Payout'],
    }),

    /* ---------------- dropdown options ---------------- */

    /**
     * Every pick-list in the platform, in one document.
     *
     * The lead form, the pipeline columns and the user roles all read this, so it is
     * asked for by a single cache key and one request serves the whole app.
     */
    options: build.query({
      query: () => '/options',
      providesTags: ['Options'],
    }),

    /** Popular predefined ∪ seen in the ledger ∪ custom, already merged server-side. */
    testRegistry: build.query({
      query: () => '/options/tests/registry',
      providesTags: ['Options'],
    }),

    addOption: build.mutation({
      query: ({ key, value }) => ({ url: `/options/${key}`, method: 'POST', body: { value } }),
      invalidatesTags: ['Options'],
    }),

    removeOption: build.mutation({
      query: ({ key, value }) => ({
        url: `/options/${key}/${encodeURIComponent(value)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Options'],
    }),

    resetOption: build.mutation({
      query: (key) => ({ url: `/options/${key}/reset`, method: 'POST' }),
      invalidatesTags: ['Options'],
    }),

    /* ---------------- influencer commission tiers ---------------- */


    /**
     * Every write here changes what a partner is owed, so each one invalidates
     * `Payout` as well — the KPI band and the overview dashboard read that.
     */
    createInfluencer: build.mutation({
      query: (body) => ({ url: '/influencers', method: 'POST', body }),
      invalidatesTags: ['Influencers', 'Payout'],
    }),

    updateInfluencer: build.mutation({
      query: ({ id, ...body }) => ({ url: `/influencers/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Influencers', 'Payout'],
    }),

    deleteInfluencer: build.mutation({
      query: (id) => ({ url: `/influencers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Influencers', 'Payout'],
    }),

    /* ---------------- team / users ---------------- */


    /** Paginated team list. `params` mirrors the query string one-for-one. */
    users: build.query({
      query: (params = {}) => `/users${qs(params)}`,
      providesTags: ['Users'],
    }),

    createUser: build.mutation({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      invalidatesTags: ['Users'],
    }),

    updateUser: build.mutation({
      query: ({ id, ...body }) => ({ url: `/users/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Users'],
    }),

    /** Enable / disable in one call — the server owns which way it flips. */
    toggleUser: build.mutation({
      query: (id) => ({ url: `/users/${id}/toggle`, method: 'PATCH' }),
      invalidatesTags: ['Users'],
    }),

    /** Admin sets a new password for someone else; no cache to invalidate. */
    resetUserPassword: build.mutation({
      query: ({ id, password }) => ({ url: `/users/${id}/reset-password`, method: 'POST', body: { password } }),
    }),

    deleteUser: build.mutation({
      query: (id) => ({ url: `/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Users'],
    }),

    /* ---------------- accounts ---------------- */


    /** Paginated account book. `params` mirrors the query string one-for-one. */
    accounts: build.query({
      query: (params = {}) => `/accounts${qs(params)}`,
      providesTags: ['Accounts'],
    }),

    /** KPI tiles, counted over the whole book rather than the visible page. */
    accountsSummary: build.query({
      query: ({ channel } = {}) => `/accounts/summary${qs({ channel })}`,
      providesTags: ['Accounts'],
    }),

    /**
     * Last 12 months of billed revenue for one account, aggregated from the ledger.
     * Kept out of the list response — it is only worth computing when a row is opened.
     */
    accountMetrics: build.query({
      query: (id) => `/accounts/${id}/metrics`,
      providesTags: ['Accounts'],
    }),

    deleteAccount: build.mutation({
      query: (id) => ({ url: `/accounts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Accounts'],
    }),

    /* ---------------- leads pipeline ---------------- */

    /** Paginated pipeline. `params` mirrors the query string one-for-one. */
    leads: build.query({
      query: (params = {}) => `/leads${qs(params)}`,
      providesTags: ['Leads'],
    }),

    /** KPI tiles + per-stage counts, unaffected by the current page. */
    leadsSummary: build.query({
      query: ({ channel } = {}) => `/leads/summary${qs({ channel })}`,
      providesTags: ['Leads'],
    }),

    createLead: build.mutation({
      query: (body) => ({ url: '/leads', method: 'POST', body }),
      invalidatesTags: ['Leads'],
    }),

    updateLead: build.mutation({
      query: ({ id, ...body }) => ({ url: `/leads/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Leads'],
    }),

    /**
     * Moving a card between columns is the one write that has to feel instant, so the
     * cached list is patched up front and rolled back if the server disagrees.
     * `listArgs` is the exact arg object the visible `leads` query was made with —
     * without it there is no way to name the cache entry holding that card.
     */
    setLeadStage: build.mutation({
      query: ({ id, stage }) => ({ url: `/leads/${id}/stage`, method: 'PATCH', body: { stage } }),
      async onQueryStarted({ id, stage, listArgs }, { dispatch, queryFulfilled }) {
        if (!listArgs) return;
        const patch = dispatch(
          api.util.updateQueryData('leads', listArgs, (draft) => {
            const lead = draft.items?.find((l) => l.id === id);
            if (lead) lead.stage = stage;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['Leads'],
    }),

    deleteLead: build.mutation({
      query: (id) => ({ url: `/leads/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Leads'],
    }),

    /** Promotes a won lead: it leaves the pipeline and lands in accounts. */
    convertLead: build.mutation({
      query: (id) => ({ url: `/leads/${id}/convert`, method: 'POST' }),
      invalidatesTags: ['Leads', 'Accounts'],
    }),

    /* ---------------- geo / condition analysis ---------------- */

    /** Vocabulary and district tile coordinates the filter bar is built from. */
    geoMeta: build.query({
      query: () => '/geo/meta',
      providesTags: ['Geo'],
    }),

    /** One value per district for the choropleth, plus the legend range. */
    geoMap: build.query({
      query: ({ mode, key, gender, age } = {}) => `/geo/map${qs({ mode, key, gender, age })}`,
      providesTags: ['Geo'],
    }),

    /** Drill-down: mandals ranked, plus the district's full profile. */
    geoDistrict: build.query({
      query: ({ district, mode, key, gender, age }) =>
        `/geo/districts/${encodeURIComponent(district)}${qs({ mode, key, gender, age })}`,
      providesTags: ['Geo'],
    }),

    /** One level deeper: cities within a mandal. */
    geoMandal: build.query({
      query: ({ mandal, district, mode, key, gender, age }) =>
        `/geo/mandals/${encodeURIComponent(mandal)}${qs({ district, mode, key, gender, age })}`,
      providesTags: ['Geo'],
    }),

    /** Age band × gender for the current scope. */
    geoDemographics: build.query({
      query: ({ district, mandal, mode, key } = {}) => `/geo/demographics${qs({ district, mandal, mode, key })}`,
      providesTags: ['Geo'],
    }),

    /** Highest-prevalence locality per condition or test. */
    geoHotspots: build.query({
      query: ({ mode, gender, age, minPatients } = {}) => `/geo/hotspots${qs({ mode, gender, age, minPatients })}`,
      providesTags: ['Geo'],
    }),

    /** Every key against every district, each row carrying its own range. */
    geoHeatmap: build.query({
      query: ({ mode, gender, age } = {}) => `/geo/heatmap${qs({ mode, gender, age })}`,
      providesTags: ['Geo'],
    }),
  }),
});

export const {
  useSummaryQuery,
  useTransactionsQuery,
  useTransactionFiltersQuery,
  usePayoutQuery,
  useLeadsQuery,
  useLeadsSummaryQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useSetLeadStageMutation,
  useDeleteLeadMutation,
  useConvertLeadMutation,
  useOptionsQuery,
  useTestRegistryQuery,
  useAddOptionMutation,
  useRemoveOptionMutation,
  useResetOptionMutation,
  useCreateInfluencerMutation,
  useUpdateInfluencerMutation,
  useDeleteInfluencerMutation,
  useUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useToggleUserMutation,
  useResetUserPasswordMutation,
  useDeleteUserMutation,
  useAccountsQuery,
  useAccountsSummaryQuery,
  useAccountMetricsQuery,
  useDeleteAccountMutation,
  useGeoMetaQuery,
  useGeoMapQuery,
  useGeoDistrictQuery,
  useGeoMandalQuery,
  useGeoDemographicsQuery,
  useGeoHotspotsQuery,
  useGeoHeatmapQuery,
} = api;
